import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Breathing room kept between a popover and the window edges. */
const EDGE = 8;

/**
 * Where a popover sits, in viewport coordinates.
 *
 * Right-aligned under its button, then pulled back inside the window if that
 * would hang it off either edge — the header buttons sit near the left of a
 * narrow panel, so the naive placement runs off the left.
 */
function place(button, preferredWidth) {
  const rect = button.getBoundingClientRect();
  const width = Math.min(preferredWidth, window.innerWidth - EDGE * 2);
  const left = Math.min(Math.max(rect.right - width, EDGE), window.innerWidth - width - EDGE);
  return { top: rect.bottom + 6, left, width };
}

/**
 * A header-button popover: open state, placement, and closing on an outside
 * click or Escape.
 *
 * The list is expected to be portalled to `document.body` — the inbox header
 * clips its overflow — which is why `list` is checked separately from `box`.
 */
export function usePopover(width) {
  const [open, setOpen] = useState(false);
  const [spot, setSpot] = useState(null);
  const box = useRef(null);
  const trigger = useRef(null);
  const list = useRef(null);

  /* Placed before paint, and kept there while the window moves under it. */
  useLayoutEffect(() => {
    if (!open || !trigger.current) return undefined;
    const update = () => setSpot(place(trigger.current, width));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, width]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      const inside = box.current?.contains(event.target) || list.current?.contains(event.target);
      if (!inside) setOpen(false);
    };
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return { open, setOpen, spot: open ? spot : null, box, trigger, list };
}

export default usePopover;
